import React, { useEffect, Suspense, lazy } from "react";
import "./App.css";
import "./index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AnimatePresence, motion } from "framer-motion";

import Layout from "./components/Layout";
import Home from "./pages/Home"; // hero is critical — keep eager
import MaintenancePage from "./pages/MaintenancePage";

// ─── MAINTENANCE MODE ───────────────────────────────────────────────────────
// Set to true to show the maintenance page on all public routes.
// Admin panel (/admin) remains accessible at all times.
const MAINTENANCE_MODE = true;
// ────────────────────────────────────────────────────────────────────────────

// Lazy-loaded secondary routes (reduces initial JS bundle)
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Kits = lazy(() => import("./pages/Kits"));
const QueSon = lazy(() => import("./pages/QueSon"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const LearningCenter = lazy(() => import("./pages/LearningCenter"));
const GuideDetail = lazy(() => import("./pages/GuideDetail"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Legal = lazy(() => import("./pages/Legal"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin: lazy + ssr-skip (only loaded when accessing /admin)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminKits = lazy(() => import("./pages/admin/AdminKits"));
const AdminDiscounts = lazy(() => import("./pages/admin/AdminDiscounts"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminGuides = lazy(() => import("./pages/admin/AdminGuides"));
const AdminProductsMasivo = lazy(() => import("./pages/admin/AdminProductsMasivo"));
const AdminConfig = lazy(() => import("./pages/admin/AdminConfig"));
const AdminAccount = lazy(() => import("./pages/admin/AdminAccount"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));

import { ConfigProvider } from "./lib/config";
import { AuthProvider } from "./lib/auth";
import { DrawerProvider } from "./lib/DrawerContext";
import TrackingScripts from "./components/TrackingScripts";
import CustomCursor from "./components/CustomCursor";
import PurchaseDrawer from "./components/PurchaseDrawer";

const Page = ({ children }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
    {children}
  </motion.div>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
};

// Lightweight fallback used while lazy chunks load
const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" aria-label="Cargando…">
    <div className="w-8 h-8 border-2 border-[#22222B] border-t-[#00DF82] rounded-full animate-spin" />
  </div>
);

const PublicRoutes = () => {
  const location = useLocation();

  // Show maintenance page on all public routes when MAINTENANCE_MODE is active
  if (MAINTENANCE_MODE) {
    return <MaintenancePage />;
  }

  return (
    <Layout>
      <AnimatePresence mode="wait">
        <Suspense fallback={<RouteFallback />}>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Page><Home /></Page>} />
            <Route path="/productos" element={<Page><Products /></Page>} />
            <Route path="/productos/:slug" element={<Page><ProductDetail /></Page>} />
            <Route path="/kits-y-pedidos-especiales" element={<Page><Kits /></Page>} />
            <Route path="/que-son-los-peptidos" element={<Page><QueSon /></Page>} />
            <Route path="/blog" element={<Page><Blog /></Page>} />
            <Route path="/blog/:slug" element={<Page><BlogDetail /></Page>} />
            <Route path="/guias" element={<Page><LearningCenter /></Page>} />
            <Route path="/guias/:slug" element={<Page><GuideDetail /></Page>} />
            <Route path="/faq" element={<Page><FAQ /></Page>} />
            <Route path="/contacto" element={<Page><Contact /></Page>} />
            <Route path="/aviso-legal" element={<Page><Legal /></Page>} />
            <Route path="*" element={<Page><NotFound /></Page>} />
          </Routes>
        </Suspense>
      </AnimatePresence>
    </Layout>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ConfigProvider>
        <DrawerProvider>
          <AuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <CustomCursor />
              <TrackingScripts />
              <PurchaseDrawer />
              <Toaster theme="dark" position="bottom-center" toastOptions={{ style: { background: "#12121A", border: "1px solid #22222B", color: "#EDEDED" } }} />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="productos" element={<AdminProducts />} />
                    <Route path="productos-masivo" element={<AdminProductsMasivo />} />
                    <Route path="kits" element={<AdminKits />} />
                    <Route path="descuentos" element={<AdminDiscounts />} />
                    <Route path="leads" element={<AdminLeads />} />
                    <Route path="blog" element={<AdminBlog />} />
                    <Route path="guias" element={<AdminGuides />} />
                    <Route path="configuracion" element={<AdminConfig />} />
                    <Route path="cuenta" element={<AdminAccount />} />
                  </Route>
                  <Route path="/*" element={<PublicRoutes />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </AuthProvider>
        </DrawerProvider>
      </ConfigProvider>
    </HelmetProvider>
  );
}

export default App;
